import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { fetchUserReferenceStatus, fetchUserCode } from "@/lib/api";
import { toast } from "sonner";
import {
  Link as LinkIcon,
  Copy,
  ShareNetwork,
  QrCode,
  WhatsappLogo,
  TelegramLogo,
  X,
} from "@phosphor-icons/react";

export const ReferralDialog = ({ open, onOpenChange }) => {
  const [showQr, setShowQr] = useState(false);

  const { data: code, isLoading: loadingCode } = useQuery({
    queryKey: ["user-code"],
    queryFn: fetchUserCode,
    enabled: open,
  });
  const { data: status, isLoading: loadingStatus } = useQuery({
    queryKey: ["user-referral-status"],
    queryFn: fetchUserReferenceStatus,
    enabled: open,
  });

  const isLoading = loadingCode || loadingStatus;
  const link = code ? `https://emlak.pro/register?ref=${code}` : "";
  const shareText = "Əmlak PRO-ya qoşul!";

  const doCopy = () => {
    if (!link) return;
    navigator.clipboard.writeText(link);
    toast.success("Referans linki kopyalandı");
  };

  const shareToWhatsapp = () => {
    if (!link) return;
    const url = `https://api.whatsapp.com/send?text=${encodeURIComponent(`${shareText} ${link}`)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const shareToTelegram = () => {
    if (!link) return;
    const url = `https://t.me/share/url?url=${encodeURIComponent(link)}&text=${encodeURIComponent(shareText)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const StatSkeleton = () => (
    <div className="h-4 w-6 rounded bg-muted animate-pulse" />
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100vw-2rem)] sm:max-w-md p-0 gap-0 overflow-hidden max-h-[90vh] flex flex-col rounded-2xl border border-border">
        <div className="px-4 sm:px-5 py-3.5 flex items-center border-b border-border/60 bg-secondary/60 shrink-0">
          <div className="flex items-center gap-2 font-semibold text-sm sm:text-base">
            <LinkIcon size={16} weight="bold" className="text-primary shrink-0" />
            <span>Referans proqramı</span>
          </div>
        </div>
        <DialogHeader className="sr-only">
          <DialogTitle>Referans proqramı</DialogTitle>
        </DialogHeader>

        <div className="p-4 sm:p-5 space-y-4 overflow-y-auto">
          <div>
            <div className="text-xs sm:text-sm font-semibold mb-1.5">Referans linkiniz</div>
            <div className="flex items-center gap-2">
              <div
                className="flex-1 min-w-0 h-10 rounded-lg border border-border bg-secondary/40 px-2.5 sm:px-3 flex items-center text-xs sm:text-sm font-mono truncate"
                data-testid="ref-link"
              >
                {loadingCode ? (
                  <div className="h-3.5 w-full max-w-[160px] rounded bg-muted animate-pulse" />
                ) : (
                  <span className="truncate">{link}</span>
                )}
              </div>

              {/* COPY DÜYMƏSİ */}
              <button
                onClick={doCopy}
                disabled={isLoading}
                data-testid="ref-copy-btn"
                className="w-10 h-10 shrink-0 rounded-lg border border-border bg-card hover:border-primary hover:text-primary flex items-center justify-center transition disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:border-border disabled:hover:text-current active:scale-95"
                aria-label="Copy"
              >
                <Copy size={16} weight="bold" />
              </button>

              {/* PAYLAŞ DÜYMƏSİ VƏ DROPDOWN MENU */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    disabled={isLoading}
                    data-testid="ref-share-btn"
                    className="w-10 h-10 shrink-0 rounded-lg bg-primary hover:bg-primary/90 text-primary-foreground flex items-center justify-center transition disabled:opacity-50 disabled:cursor-not-allowed active:scale-95"
                    aria-label="Share"
                  >
                    <ShareNetwork size={16} weight="bold" />
                  </button>
                </DropdownMenuTrigger>

                <DropdownMenuContent
                  align="end"
                  className="w-52 sm:w-56 rounded-2xl p-1.5 shadow-xl border border-border/80 bg-popover text-popover-foreground z-50"
                >
                  <DropdownMenuItem
                    onClick={() => setShowQr(true)}
                    className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl cursor-pointer font-semibold text-xs sm:text-sm transition-colors hover:bg-slate-100 hover:text-black focus:bg-slate-100 focus:text-black dark:hover:bg-slate-200 dark:hover:text-black dark:focus:bg-slate-200 dark:focus:text-black"
                  >
                    <QrCode size={18} weight="bold" />
                    <span>QR kod göstər</span>
                  </DropdownMenuItem>

                  <DropdownMenuItem
                    onClick={shareToWhatsapp}
                    className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl cursor-pointer font-semibold text-xs sm:text-sm transition-colors text-emerald-600 hover:bg-emerald-100 hover:text-black focus:bg-emerald-100 focus:text-black dark:text-emerald-400 dark:hover:bg-emerald-500/20 dark:hover:text-black dark:focus:bg-emerald-500/20 dark:focus:text-black"
                  >
                    <WhatsappLogo size={18} weight="fill" className="text-emerald-500" />
                    <span>WhatsApp ilə paylaş</span>
                  </DropdownMenuItem>

                  <DropdownMenuItem
                    onClick={shareToTelegram}
                    className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl cursor-pointer font-semibold text-xs sm:text-sm transition-colors text-sky-600 hover:bg-sky-100 hover:text-black focus:bg-sky-100 focus:text-black dark:text-sky-400 dark:hover:bg-sky-500/20 dark:hover:text-black dark:focus:bg-sky-500/20 dark:focus:text-black"
                  >
                    <TelegramLogo size={18} weight="fill" className="text-sky-500" />
                    <span>Telegram ilə paylaş</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>

          {/* QR KOD İÇİN QUTU */}
          {showQr && (
            <div className="relative flex flex-col items-center justify-center p-4 rounded-xl border border-border bg-card animate-in fade-in zoom-in-95 duration-150">
              <button
                type="button"
                onClick={() => setShowQr(false)}
                className="absolute top-2 right-2 p-1 text-muted-foreground hover:text-foreground rounded-md transition"
              >
                <X size={16} />
              </button>
              <img
                src={`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(link)}`}
                alt="QR Code"
                className="w-28 h-28 sm:w-36 sm:h-36 rounded-lg border p-1 bg-white"
              />
              <span className="text-[11px] sm:text-xs text-muted-foreground mt-2 text-center">
                Skan edərək keçid edin
              </span>
            </div>
          )}

          <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
            Referans kodunuzu dostlarınıza göndərin və paket alan istifadəçilərə görə bonus qazanın.
          </p>

          <div className="rounded-xl overflow-hidden border border-border/60 divide-y divide-border/60 text-xs sm:text-sm">
            <div className="flex items-center justify-between px-3.5 py-2.5 sm:px-4 sm:py-3 bg-secondary/40">
              <span className="text-muted-foreground">Referans sayı</span>
              <span className="font-bold price-tabular text-primary" data-testid="ref-count">
                {loadingStatus ? <StatSkeleton /> : (status?.referralUserCount ?? 0)}
              </span>
            </div>
            <div className="flex items-center justify-between px-3.5 py-2.5 sm:px-4 sm:py-3 bg-secondary/40">
              <span className="text-muted-foreground">Ödəniş sayı</span>
              <span className="font-bold price-tabular text-primary" data-testid="ref-pay-count">
                {loadingStatus ? <StatSkeleton /> : (status?.referralUserPaymentCount ?? 0)}
              </span>
            </div>
            <div className="flex items-center justify-between px-3.5 py-2.5 sm:px-4 sm:py-3 bg-secondary/40">
              <span className="text-muted-foreground">Qazanılan bonus</span>
              <span className="font-bold price-tabular text-primary" data-testid="ref-bonus">
                {loadingStatus ? <StatSkeleton /> : `${status?.referralBonusDays ?? 0} gün`}
              </span>
            </div>
          </div>
          <p className="text-[11px] sm:text-xs text-muted-foreground">
            Qazanılan müddət avtomatik aktiv paketinizə əlavə olunur.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};